import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { getReview, submitReview } from "../services/reviews";
import { StarRating } from "./StarRating";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

// MVP: lab reviews the selected designer once the case is Completed.
// Schema is bidirectional-ready; this component only exposes the lab->designer flow for now.
export const CaseReview = ({ caseItem, isOwner, isSelectedDesigner, user, profile }) => {
  const { t } = useTranslation();
  const [labReview, setLabReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);

  const caseId = caseItem.id;
  const completed = caseItem.status === "Completed";
  const ownerId = caseItem.ownerId;

  const load = useCallback(async () => {
    if (!completed || !ownerId) return;
    const r = await getReview(caseId, ownerId);
    setLabReview(r);
    if (r && isOwner) {
      setRating(r.rating || 0);
      setComment(r.comment || "");
    }
  }, [caseId, ownerId, completed, isOwner]);

  useEffect(() => { load(); }, [load]);

  if (!completed || !caseItem.selectedDesignerId) return null;

  const submit = async () => {
    if (rating < 1) {
      toast.error(t("reviews.selectStars"));
      return;
    }
    setBusy(true);
    try {
      const existed = await submitReview(caseId, {
        raterId: user.uid,
        raterRole: "lab",
        raterName: profile?.name || user.email,
        revieweeId: caseItem.selectedDesignerId,
        revieweeRole: "designer",
        rating,
        comment,
      });
      toast.success(existed ? t("reviews.updated") : t("reviews.submitted"));
      await load();
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6" data-testid="case-review">
      <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
        <Star className="h-5 w-5" /> {t("reviews.title")}
      </h3>

      {/* Read view of the submitted review (both parties see it) */}
      {labReview && (
        <div className="mb-4 rounded-md border border-border bg-secondary/30 p-4" data-testid="lab-review-display">
          <p className="mb-1.5 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            {isOwner ? t("reviews.yourReview") : t("reviews.labReview")}
          </p>
          <StarRating value={labReview.rating} readOnly testid="lab-review-stars" />
          {labReview.comment && <p className="mt-2 text-sm text-foreground">{labReview.comment}</p>}
        </div>
      )}

      {/* Lab: submit or edit the review */}
      {isOwner && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">{t("reviews.completedHint")}</p>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t("reviews.yourRating")}</label>
            <StarRating value={rating} onChange={setRating} readOnly={false} size="h-7 w-7" testid="rating-input" />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t("reviews.commentOptional")}</label>
            <Textarea rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("reviews.commentPlaceholder")} data-testid="rating-comment" />
          </div>
          <Button className="w-full rounded-full bg-brand text-white hover:bg-brand/90" onClick={submit} disabled={busy} data-testid="submit-rating">
            {labReview ? t("reviews.update") : t("reviews.submit")}
          </Button>
        </div>
      )}

      {/* Selected designer with no review yet */}
      {isSelectedDesigner && !labReview && (
        <p className="text-sm text-muted-foreground" data-testid="no-review-yet">{t("reviews.noRatings")}</p>
      )}
    </div>
  );
};
